import classNames from 'classnames';
import { ReactNode, useEffect, useRef, useState } from 'react';

import useMeasure from '../../hooks/useMeasure';
import ErrorList from '../../types/UI/ErrorList';
import ErrorMessage from '../ErrorMessage/ErrorMessage';
import HiddenField from '../HiddenField/HiddenField';
import Inline from '../Inline/Inline';
import Stack from '../Stack/Stack';

import RadioBarButton from './components/RadioBarButton/RadioBarButton';

import styles from './RadioBar.module.css';

export type Option<T> = {
    value: T;
    label: ReactNode;
    highlight?: boolean;
};

type RadioBarProps<T> = {
    options: Array<Option<T>>;
    variant?: 'default' | 'boxed';
    name?: string;
    onChange?: (value: T) => void;
    defaultValue?: T;
    label?: ReactNode;
    value?: T;
    errors?: ErrorList;
    size?: 'compact' | 'default' | 'large';
    withBackground?: boolean;
};

const RadioBar = <T extends string | number>({
    options,
    name,
    label,
    variant = 'default',
    onChange,
    defaultValue,
    value,
    errors,
    size = 'default',
    withBackground = false,
}: RadioBarProps<T>) => {
    const [currentValue, setCurrentValue] = useState(value ?? defaultValue);
    const firstElementRef = useRef<HTMLButtonElement | null>(null);
    const clickedElementRef = useRef<HTMLButtonElement | null>(null);
    const chosenElementDimensions = useMeasure(
        clickedElementRef.current ? clickedElementRef : firstElementRef
    );

    useEffect(() => {
        if (value !== currentValue && typeof value !== 'undefined') {
            setCurrentValue(value);
        }
    }, [value, currentValue]);

    const handleClick = (value: T, el: HTMLButtonElement) => {
        if (value === currentValue) {
            return;
        }

        setCurrentValue(value);
        clickedElementRef.current = el;

        onChange && onChange(value);
    };

    return (
        <Stack space="small">
            {name && <HiddenField name={name} value={currentValue ?? ''} />}
            <Inline vertical="middle">
                {label}

                <div
                    className={classNames(styles.OptionWrapper, {
                        [styles.OptionWrapper_background]: withBackground,
                    })}
                >
                    {firstElementRef.current && (
                        <div
                            className={classNames(styles.CurrentHighlighter, {
                                [styles.CurrentHighlighter_default]:
                                    variant === 'default',
                                [styles.CurrentHighlighter_boxed]:
                                    variant === 'boxed',
                            })}
                            style={{
                                left: chosenElementDimensions.x,
                                width: chosenElementDimensions.width,
                            }}
                        />
                    )}

                    {options.map((option) => (
                        <RadioBarButton
                            key={option.value}
                            variant={variant}
                            size={size}
                            currentValue={currentValue}
                            option={option}
                            onClick={handleClick}
                            buttonRef={
                                currentValue === option.value
                                    ? firstElementRef
                                    : undefined
                            }
                        />
                    ))}
                </div>
            </Inline>

            <ErrorMessage>{errors}</ErrorMessage>
        </Stack>
    );
};

export default RadioBar;
